<template>
  <div class="tree-select-panel">
    <div class="flex flex-wrap justify-content-end gap-2 p-2">
      <ToggleButton
        v-if="showOnlySelectedControl && multiple"
        v-model="showOnlySelected"
        :on-label="t('components.shared.searchSelect.TreeSelect.onlySelected')"
        :off-label="t('components.shared.searchSelect.TreeSelect.onlySelected')"
      >
        <template #icon>
          <i class="fas" :class="showOnlySelected ? 'fa-square-check' : 'fa-square'" />
        </template>
      </ToggleButton>
      <LISAButton
        icon="fas fa-plus"
        :label="t('components.shared.searchSelect.TreeSelect.expandAll')"
        size="small"
        :disabled="showOnlySelected"
        @click="$emit('expandAll')"
      />
      <LISAButton
        icon="fas fa-minus"
        :label="t('components.shared.searchSelect.TreeSelect.collapseAll')"
        size="small"
        :disabled="showOnlySelected"
        @click="$emit('collapseAll')"
      />
    </div>

    <div class="tree-select-panel__filter">
      <IconField>
        <InputText
          v-model="searchText"
          class="w-100"
          :disabled="disabled || showOnlySelected"
          :placeholder="t('components.shared.searchSelect.TreeSelect.search')"
        />
        <InputIcon class="pi pi-search" />
      </IconField>
    </div>

    <div class="tree-select-panel__tree" :style="{ height }">
      <div v-if="showOnlySelected" class="h-100 overflow-y-auto">
        <TreeSelectItem
          v-for="node in selectedNodes"
          :key="node.key"
          v-model:collapsed="collapsed"
          v-model:selected="selected"
          class="w-100"
          :multiple="multiple"
          :hide-checkbox="hideCheckbox"
          :hide-path="hidePath"
          :node="node"
          plain
        >
          <template #controls>
            <slot name="itemControls" :node="node" />
          </template>
        </TreeSelectItem>
      </div>
      <VirtualScroller
        v-else
        ref="virtualScrollerRef"
        :items="nodes"
        :item-size="TREE_SELECT_ITEM_SIZE"
        class="h-100"
      >
        <template #item="{ item: node }">
          <TreeSelectItem
            v-model:collapsed="collapsed"
            v-model:selected="selected"
            :multiple="multiple"
            :hide-checkbox="hideCheckbox"
            :hide-path="hidePath"
            :node="node"
          >
            <template #controls>
              <slot name="itemControls" :node="node" />
            </template>
          </TreeSelectItem>
        </template>
      </VirtualScroller>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useTranslation } from "@/language/translation";

import { ref } from "vue";
import IconField from "primevue/iconfield";
import InputIcon from "primevue/inputicon";
import InputText from "primevue/inputtext";
import ToggleButton from "primevue/togglebutton";
import VirtualScroller from "primevue/virtualscroller";
import LISAButton from "../../primevue/LISAButton.vue";
import type { TreeNodeItem } from "./TreeSelectHelper";
import TreeSelectItem from "./TreeSelectItem.vue";

const TREE_SELECT_ITEM_SIZE = 36;

const props = withDefaults(
  defineProps<{
    nodes: TreeNodeItem[];
    selectedNodes: TreeNodeItem[];
    multiple?: boolean;
    hideCheckbox?: boolean;
    hidePath?: boolean;
    disabled?: boolean;
    showOnlySelectedControl?: boolean;
    height?: string;
  }>(),
  {
    multiple: false,
    hideCheckbox: false,
    hidePath: false,
    disabled: false,
    showOnlySelectedControl: true,
    height: "50vh",
  },
);

defineEmits<{
  expandAll: [];
  collapseAll: [];
}>();

const selected = defineModel<Record<string, boolean>>("selected", { required: true });
const collapsed = defineModel<Record<string, boolean>>("collapsed", { required: true });
const searchText = defineModel<string>("searchText", { required: true });
const showOnlySelected = defineModel<boolean>("showOnlySelected", { required: true });
const virtualScrollerRef = ref<InstanceType<typeof VirtualScroller> | null>(null);

function scrollToKey(key?: string) {
  if (key == null) return;
  const index = props.nodes.findIndex((node) => node.key === key);
  if (index < 0) return;

  const renderedRange = virtualScrollerRef.value?.getRenderedRange();
  const visibleItemCount =
    renderedRange == null ? 0 : renderedRange.viewport.last - renderedRange.viewport.first;
  virtualScrollerRef.value?.scrollToIndex(Math.max(index - Math.floor(visibleItemCount / 2), 0));
}

defineExpose({ scrollToKey });

const { t } = useTranslation();
</script>

<style scoped lang="scss">
.tree-select-panel {
  display: flex;
  min-height: 0;
  flex-direction: column;
}

.tree-select-panel__filter {
  padding: 0 0.75rem 0.75rem;
  border-bottom: 1px solid var(--p-content-border-color);
}

.tree-select-panel__tree {
  padding: 0.35rem;
}
</style>
